import React, { useState, useEffect, useRef } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import ColorPicker from "../../components/ColorPicker"

const ButtonStyleDialog = ({
  buttonElement = null,
  onClose,
}) => {
  const dialogRef = useRef(null)
  const [showBackgroundColorPicker, setShowBackgroundColorPicker] = useState(false)
  const [showTextColorPicker, setShowTextColorPicker] = useState(false)
  const backgroundColorButtonRef = useRef(null)
  const textColorButtonRef = useRef(null)
  const [customStyles, setCustomStyles] = useState({
    backgroundColor: "",
    color: "",
    borderRadius: "",
    padding: "",
    fontSize: "",
    fontWeight: "",
    border: "",
    boxShadow: "",
    width: "",
    height: "",
    margin: "",
  })

  const handleSubmit = () => {
    if (buttonElement) {
      // Apply custom styles to the button
      Object.entries(customStyles).forEach(([property, value]) => {
        if (value && value.trim() !== "") {
          buttonElement.style[property] = value.trim()
        }
      })
    }

    onClose({ applied: true })
  }

  const handleReset = () => {
    if (buttonElement) {
      // Clear all custom styles
      Object.keys(customStyles).forEach((property) => {
        buttonElement.style[property] = ""
      })
    }

    // Reset form
    setCustomStyles({
      backgroundColor: "",
      color: "",
      borderRadius: "",
      padding: "",
      fontSize: "",
      fontWeight: "",
      border: "",
      boxShadow: "",
      width: "",
      height: "",
      margin: "",
    })
  }

  // Load current styles when dialog opens
  useEffect(() => {
    if (buttonElement) {
      const computedStyles = window.getComputedStyle(buttonElement)
      const currentStyles = {
        backgroundColor: buttonElement.style.backgroundColor || "",
        color: buttonElement.style.color || "",
        borderRadius: buttonElement.style.borderRadius || "",
        padding: buttonElement.style.padding || "",
        fontSize: buttonElement.style.fontSize || "",
        fontWeight: buttonElement.style.fontWeight || "",
        border: buttonElement.style.border || "",
        boxShadow: buttonElement.style.boxShadow || "",
        width: buttonElement.style.width || "",
        height: buttonElement.style.height || "",
        margin: buttonElement.style.margin || "",
      }
      setCustomStyles(currentStyles)
    }
  }, [buttonElement])

  // Handle clicking outside dialog
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking inside color pickers
      if (event.target.closest('.color-picker-container') ||
          event.target.closest('[data-color-picker="true"]')) {
        return
      }

      // Don't close if clicking the color picker buttons
      if (backgroundColorButtonRef.current && backgroundColorButtonRef.current.contains(event.target)) {
        return
      }
      if (textColorButtonRef.current && textColorButtonRef.current.contains(event.target)) {
        return
      }

      if (dialogRef.current && !dialogRef.current.contains(event.target)) {
        onClose(null)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [onClose])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose(null)
      }
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      style={{ zIndex: 1000 }}
    >
      <div
        ref={dialogRef}
        className="w-full max-w-2xl max-h-[80vh] flex flex-col bg-admin_paper md:rounded-lg mx-4"
      >
        {/* Dialog Header - Fixed at top */}
        <div className="px-6 py-4 border-b border-admin_bordered flex-shrink-0">
          <Typography 
            component="h1"
            variant="admin_h4"
            className="text-admin_text"
          >
            Advanced Button Styling
          </Typography>
        </div>

        {/* Dialog Content - Scrollable */}
        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper p-6">
          {/* Colors Section */}
          <div className="mb-6">
            <Typography 
              component="h2"
              variant="admin_label"
              className="mb-4"
            >
              Colors & Appearance
            </Typography>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-admin_text mb-2">
                  Background Color
                </label>

                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={customStyles.backgroundColor}
                    onChange={(e) => setCustomStyles({ ...customStyles, backgroundColor: e.target.value })}
                    placeholder="e.g., #ff0000, red, rgba(255,0,0,0.5)"
                    variant="admin"
                    className="flex-1"
                  />

                  <button
                    ref={backgroundColorButtonRef}
                    type="button"
                    onClick={() => setShowBackgroundColorPicker(!showBackgroundColorPicker)}
                    className="w-10 h-10 rounded-lg border-2 border-admin_bordered bg-white shadow-sm hover:shadow-md transition-shadow"
                    style={{
                      backgroundColor: customStyles.backgroundColor || 'transparent',
                      backgroundImage: !customStyles.backgroundColor ?
                        'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)' :
                        'none',
                      backgroundSize: '8px 8px',
                      backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px'
                    }}
                    title="Choose background color"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-admin_text mb-2">
                  Text Color
                </label>

                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={customStyles.color}
                    onChange={(e) => setCustomStyles({ ...customStyles, color: e.target.value })}
                    placeholder="e.g., #ffffff, white"
                    variant="admin"
                    className="flex-1"
                  />
                  
                  <button
                    ref={textColorButtonRef}
                    type="button"
                    onClick={() => setShowTextColorPicker(!showTextColorPicker)}
                    className="w-10 h-10 rounded-lg border-2 border-admin_bordered bg-white shadow-sm hover:shadow-md transition-shadow"
                    style={{
                      backgroundColor: customStyles.color || 'transparent',
                      backgroundImage: !customStyles.color ?
                        'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)' :
                        'none',
                      backgroundSize: '8px 8px',
                      backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px'
                    }}
                    title="Choose text color"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Typography Section */}
          <div className="mb-6">
            <h4 className="text-md font-medium text-admin_text mb-4">
              Typography
            </h4>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-admin_text mb-2">
                  Font Size
                </label>
                <Input
                  type="text"
                  value={customStyles.fontSize}
                  onChange={(e) => setCustomStyles({ ...customStyles, fontSize: e.target.value })}
                  placeholder="e.g., 16px, 1rem, large"
                  variant="admin"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-admin_text mb-2">
                  Font Weight
                </label>
                <Input
                  type="text"
                  value={customStyles.fontWeight}
                  onChange={(e) => setCustomStyles({ ...customStyles, fontWeight: e.target.value })}
                  placeholder="e.g., bold, 600, normal"
                  variant="admin"
                />
              </div>
            </div>
          </div>

          {/* Spacing & Layout Section */}
          <div className="mb-6">
            <h4 className="text-md font-medium text-admin_text mb-4">Spacing & Layout</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-admin_text mb-2">
                  Padding
                </label>
                <Input
                  type="text"
                  value={customStyles.padding}
                  onChange={(e) => setCustomStyles({ ...customStyles, padding: e.target.value })}
                  placeholder="e.g., 10px, 1rem, 5px 10px"
                  variant="admin"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-admin_text mb-2">
                  Margin
                </label>
                <Input
                  type="text"
                  value={customStyles.margin}
                  onChange={(e) => setCustomStyles({ ...customStyles, margin: e.target.value })}
                  placeholder="e.g., 10px, auto, 5px 10px"
                  variant="admin"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-admin_text mb-2">
                  Width
                </label>
                <Input
                  type="text"
                  value={customStyles.width}
                  onChange={(e) => setCustomStyles({ ...customStyles, width: e.target.value })}
                  placeholder="e.g., 200px, 100%, auto"
                  variant="admin"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-admin_text mb-2">
                  Height
                </label>
                <Input
                  type="text"
                  value={customStyles.height}
                  onChange={(e) => setCustomStyles({ ...customStyles, height: e.target.value })}
                  placeholder="e.g., 40px, auto"
                  variant="admin"
                />
              </div>
            </div>
          </div>

          {/* Border & Effects Section */}
          <div className="mb-6">
            <h4 className="text-md font-medium text-admin_text mb-4">Border & Effects</h4>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-admin_text mb-2">
                  Border Radius
                </label>
                <Input
                  type="text"
                  value={customStyles.borderRadius}
                  onChange={(e) => setCustomStyles({ ...customStyles, borderRadius: e.target.value })}
                  placeholder="e.g., 5px, 0.5rem, 50%"
                  variant="admin"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-admin_text mb-2">
                  Border
                </label>
                <Input
                  type="text"
                  value={customStyles.border}
                  onChange={(e) => setCustomStyles({ ...customStyles, border: e.target.value })}
                  placeholder="e.g., 1px solid #ccc, 2px dashed red"
                  variant="admin"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-admin_text mb-2">
                  Box Shadow
                </label>
                <Input
                  type="text"
                  value={customStyles.boxShadow}
                  onChange={(e) => setCustomStyles({ ...customStyles, boxShadow: e.target.value })}
                  placeholder="e.g., 0 2px 4px rgba(0,0,0,0.1)"
                  variant="admin"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Dialog Footer - Fixed at bottom */}
        <div className="px-6 py-4 bg-admin_canvas border-t border-admin_bordered rounded-b-lg flex items-center justify-between flex-shrink-0">
          {/* Left side - Reset button */}
          <Button
            variant="admin_error"
            onClick={handleReset}
            type="button"
          >
            Reset Styles
          </Button>

          {/* Right side - Cancel and Apply buttons */}
          <div className="flex space-x-3">
            <Button
              variant="admin_secondary"
              onClick={() => onClose(null)}
              type="button"
            >
              Cancel
            </Button>
            <Button
              variant="admin_primary"
              type="button"
              onClick={handleSubmit}
            >
              Apply Styles
            </Button>
          </div>
        </div>
      </div>

      {/* Background Color Picker */}
      {showBackgroundColorPicker && (
        <div
          className="fixed color-picker-container"
          data-color-picker="true"
          style={{
            left: backgroundColorButtonRef.current?.getBoundingClientRect().left || 100,
            top: (backgroundColorButtonRef.current?.getBoundingClientRect().bottom || 100) + 5,
            userSelect: 'none',
            zIndex: 1001
          }}
        >
          <ColorPicker
            onColorSelect={(color) => {
              setCustomStyles({ ...customStyles, backgroundColor: color })
              setShowBackgroundColorPicker(false)
            }}
            onClose={() => setShowBackgroundColorPicker(false)}
            selectedColor={customStyles.backgroundColor}
          />
        </div>
      )}

      {/* Text Color Picker */}
      {showTextColorPicker && (
        <div
          className="fixed color-picker-container"
          data-color-picker="true"
          style={{
            left: textColorButtonRef.current?.getBoundingClientRect().left || 100,
            top: (textColorButtonRef.current?.getBoundingClientRect().bottom || 100) + 5,
            userSelect: 'none',
            zIndex: 1001
          }}
        >
          <ColorPicker
            onColorSelect={(color) => {
              setCustomStyles({ ...customStyles, color: color })
              setShowTextColorPicker(false)
            }}
            onClose={() => setShowTextColorPicker(false)}
            selectedColor={customStyles.color}
          />
        </div>
      )}
    </div>
  )
}

export default ButtonStyleDialog